import { useCallback, useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Bell, BellRing, Check } from "lucide-react";
import { toast } from "sonner";
import { formatDate } from "@/lib/api";
import { listNotifications, markNotificationsRead } from "@/lib/notificationsApi";
import { StateBlock } from "@/components/common";
import { Button } from "@/components/ui/button";
import { PushPreferenceCard } from "@/components/PushPreferenceCard";
import { Skeleton } from "./SaudeLayout";

export default function SaudeNotifications() {
  const navigate = useNavigate();
  const [data, setData] = useState(null);

  const load = useCallback(async () => {
    try {
      setData(await listNotifications("saude"));
    } catch {
      setData({ items: [], unread: 0 });
    }
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  const open = async (item) => {
    await markNotificationsRead("saude", item.id).catch(() => {});
    if (item.route) navigate(item.route);
  };

  if (!data) return <Skeleton lines={4} testId="notifications-skeleton" />;

  return (
    <div data-testid="saude-notifications-page" className="space-y-6">
      <div>
        <p className="er-label">Avisos</p>
        <h1 className="mt-3 font-display text-2xl font-medium tracking-tight text-ink-900">Notificações</h1>
      </div>

      <PushPreferenceCard
        app="saude"
        title="Avisos de novos conteúdos"
        description="Receba um aviso no celular quando publicarmos um novo conteúdo educativo."
      />

      {data.items.length === 0 ? (
        <StateBlock
          testId="notifications-empty"
          icon={Bell}
          title="Nenhum aviso por aqui"
          description="Quando publicarmos um conteúdo novo, o aviso aparece nesta lista."
        />
      ) : (
        <>
          {data.unread > 0 ? (
            <Button
              data-testid="notifications-mark-all"
              variant="outline"
              onClick={async () => {
                await markNotificationsRead("saude");
                toast.success("Avisos marcados como lidos");
                load();
              }}
              className="h-11 rounded-full"
            >
              <Check size={15} className="mr-2" /> Marcar tudo como lido
            </Button>
          ) : null}
          <div className="space-y-3">
            {data.items.map((item) => (
              <button
                key={item.id}
                type="button"
                data-testid={`notification-${item.id}`}
                onClick={() => open(item)}
                className={`er-card block w-full text-left ${item.read ? "" : "border-sage-600/40 bg-sage-50/50"}`}
              >
                <span className="flex items-start gap-3">
                  <BellRing size={17} className={item.read ? "mt-0.5 shrink-0 text-ink-400" : "mt-0.5 shrink-0 text-sage-700"} />
                  <span>
                    <span className="block font-display text-base font-medium text-ink-900">{item.title}</span>
                    <span className="mt-1 block text-sm leading-relaxed text-ink-600">{item.body}</span>
                    <span className="mt-1.5 block text-xs text-ink-400">{formatDate(item.created_at)}</span>
                  </span>
                </span>
              </button>
            ))}
          </div>
        </>
      )}
    </div>
  );
}
